import { createSelector } from "@reduxjs/toolkit";

import { selectMultiImages } from "../../../components/ImageUploader/images.slice";
import { FormId } from "../../../components/enums";
import { UIState } from "../../../store/HOC/genericUI.slice";
import { RootState } from "../../../store/rootReducer";
import { generateAddress } from "../../../utilities/forms";
import { parseDataToDDMMYYYY } from "../../../utilities/functions";

import { ComponentId as CircumstancesComponentId } from "./CircumstancesData/enums";
import { ComponentId as DamageComponentId } from "./DamageData/enums";
import { ComponentId as StatementsComponentId } from "./StatementsData/enums";
import { FormDataInputs } from "./incident.slice";
import {
  IncidentCircumstancesFormInputs,
  IncidentDamageFormInputs,
  IncidentStatementsFormInputs,
} from "./incident.slice";

export const selectIncidentCircumstancesData = (
  store: RootState
): IncidentCircumstancesFormInputs =>
  store.ac.incident.data[FormId.INCIDENT_CIRCUMSTANCES];

export const selectIncidentDamageData = (
  store: RootState
): IncidentDamageFormInputs => store.ac.incident.data[FormId.INCIDENT_DAMAGE];

export const selectIncidentStatementsData = (
  store: RootState
): IncidentStatementsFormInputs =>
  store.ac.incident.data[FormId.INCIDENT_STATEMENTS];

const selectStatementsData = (store: RootState) =>
  store.ac.incident.data[FormId.INCIDENT_STATEMENTS];

export const selectIsPerpetratorStatementSigned = createSelector(
  selectStatementsData,
  (
    statementsData: Pick<
      IncidentStatementsFormInputs,
      StatementsComponentId.INCIDENT_STATEMENTS_PERPETRATOR_STATEMENT
    >
  ) =>
    statementsData[
      StatementsComponentId.INCIDENT_STATEMENTS_PERPETRATOR_STATEMENT
    ] === "true"
);

export const selectWasPoliceInvolved = createSelector(
  selectStatementsData,
  (
    statementsData: Pick<
      IncidentStatementsFormInputs,
      StatementsComponentId.INCIDENT_STATEMENTS_POLICE_PRESENT
    >
  ) =>
    statementsData[StatementsComponentId.INCIDENT_STATEMENTS_POLICE_PRESENT] ===
    "true"
);

export const selectWasPoliceNotInvolved = createSelector(
  selectStatementsData,
  (
    statementsData: Pick<
      IncidentStatementsFormInputs,
      StatementsComponentId.INCIDENT_STATEMENTS_POLICE_PRESENT
    >
  ) =>
    statementsData[StatementsComponentId.INCIDENT_STATEMENTS_POLICE_PRESENT] ===
    "false"
);

const selectIncidentDateTime = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Data i godzina",
    value: [
      parseDataToDDMMYYYY(
        data[CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_DATE]
      ),
      data[CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_HOUR],
    ].join(", "),
  })
);

const selectIncidentPlace = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Miejsce",
    value: data[CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_PLACE],
  })
);

const selectIncidentAddress = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Miejscowość, województwo, kraj",
    value: generateAddress(data),
  })
);

const selectIncidentDescription = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Opis",
    value: data[CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_DESCRIPTION],
  })
);

const selectIncidentCircumstancesSummaryData = createSelector(
  selectIncidentDateTime,
  selectIncidentPlace,
  selectIncidentAddress,
  selectIncidentDescription,
  (dateTime, place, address, description) => [
    dateTime,
    place,
    address,
    description,
  ]
);

const selectIncidentDamageDescription = createSelector(
  selectIncidentDamageData,
  (data) => ({
    name: "Opis uszkodzeń",
    value: data[DamageComponentId.INCIDENT_DAMAGE_DESCRIPTION],
  })
);

const selectIncidentDamageImages = createSelector(
  (data: RootState) =>
    selectMultiImages(data, {
      section: DamageComponentId.INCIDENT_DAMAGE_IMAGES,
    }),
  (data) => ({
    name: "Zdjęcia uszkodzeń pojazdu",
    value: Object.values(data).map((image) => image.src as string),
  })
);

const selectIncidentDamageSummaryData = createSelector(
  selectIncidentDamageDescription,
  selectIncidentDamageImages,
  (damageDescription, damageImages) => [damageDescription, damageImages]
);

const selectPoliceDepartment = createSelector(
  selectIncidentStatementsData,
  (data) => ({
    name: "Jednostka policji",
    value: data[StatementsComponentId.INCIDENT_STATEMENTS_POLICE_DEPARTMENT],
  })
);

const selectCaseNumber = createSelector(
  selectIncidentStatementsData,
  (data) => ({
    name: "Numer sprawy",
    value: data[StatementsComponentId.INCIDENT_STATEMENTS_CASE_NUMBER],
  })
);

const selectPoliceImages = createSelector(
  (data: RootState) =>
    selectMultiImages(data, {
      section: StatementsComponentId.INCIDENT_STATEMENTS_POLICE_IMAGES,
    }),
  (data) => ({
    name: "Protokół policji",
    value: Object.values(data).map((image) => image.src as string),
  })
);

const selectIncidentPoliceStatements = createSelector(
  selectWasPoliceInvolved,
  selectPoliceDepartment,
  selectCaseNumber,
  selectPoliceImages,
  (wasPoliceInvolved, policeDepartment, caseNumber, images) => {
    if (!wasPoliceInvolved) {
      return [{ name: "Obecność policji", value: "nie" }];
    }

    return [
      { name: "Obecność policji", value: "tak" },
      policeDepartment,
      caseNumber,
      images,
    ];
  }
);

const selectPerpetratorStatementsImages = createSelector(
  (data: RootState) =>
    selectMultiImages(data, {
      section: StatementsComponentId.INCIDENT_STATEMENTS_PERPETRATOR_IMAGES,
    }),
  (data) => {
    return [
      {
        name: "Oświadczenia sprawcy",
        value: Object.values(data).map((image) => image.src as string),
      },
    ];
  }
);

const selectIncidentPerpetratorStatements = createSelector(
  selectIsPerpetratorStatementSigned,
  selectPerpetratorStatementsImages,
  (isPerpetratorStatementSigned, images) => {
    if (!isPerpetratorStatementSigned) {
      return [{ name: "Oświadczenie sprawcy", value: "brak" }];
    }

    return images;
  }
);

const selectIncidentStatementsSummaryData = createSelector(
  selectIncidentPoliceStatements,
  selectIncidentPerpetratorStatements,
  (policeStatements, prepetratorStatements) => [
    ...policeStatements,
    ...prepetratorStatements,
  ]
);

export const selectSummaryData = createSelector(
  selectIncidentCircumstancesSummaryData,
  selectIncidentDamageSummaryData,
  selectIncidentStatementsSummaryData,
  (circumstances, damages, statements) => [
    ...circumstances,
    ...damages,
    ...statements,
  ]
);

export const selectFormData = (store: RootState): FormDataInputs =>
  store.ac.incident.data;

export const selectUIData = (store: RootState): UIState => store.ac.incident.ui;
